import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarCheck, Loader2, Phone, Video } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = Array.from({ length: 16 }, (_, i) => {
  const h = 9 + Math.floor(i / 2);
  return `${String(h).padStart(2, "0")}:${i % 2 ? "30" : "00"}`;
});

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-300 focus:border-emerald-400/60 focus:bg-white/[0.06]";

function nextDays(count = 14) {
  const days = [];
  const d = new Date();
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() === 0 || d.getDay() === 6) continue;
    days.push(new Date(d));
  }
  return days;
}

const dayFmt = new Intl.DateTimeFormat("fr-CA", { weekday: "short" });
const dateFmt = new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "short" });
const fullFmt = new Intl.DateTimeFormat("fr-CA", { weekday: "long", day: "numeric", month: "long" });

export default function Booking() {
  const days = useMemo(() => nextDays(14), []);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [taken, setTaken] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [booked, setBooked] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const dateStr = (d) => d.toLocaleDateString("en-CA");

  useEffect(() => {
    if (!selectedDay) return;
    setSelectedTime(null);
    axios
      .get(`${API}/bookings`, { params: { date: dateStr(selectedDay) } })
      .then((res) => setTaken(res.data.taken || []))
      .catch(() => setTaken([]));
  }, [selectedDay]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...form,
        date: dateStr(selectedDay),
        time: selectedTime,
      });
      setBooked(res.data);
      toast.success("Rendez-vous réservé ! Vous recevrez une confirmation très bientôt.");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Ce créneau vient d'être pris — choisissez-en un autre.");
        const res = await axios.get(`${API}/bookings`, { params: { date: dateStr(selectedDay) } });
        setTaken(res.data.taken || []);
        setSelectedTime(null);
      } else {
        toast.error("Une erreur est survenue. Réessayez dans un instant.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rendez-vous" className="relative overflow-hidden py-20 lg:py-32" data-testid="booking-section">
      <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[500px] rounded-full bg-emerald-500/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20 lg:px-8">
        <Reveal>
          <p className="font-mono-label mb-4">Chapitre 05 — Parlons-en</p>
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
            Réservez votre <span className="text-emerald-400">appel découverte</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-gray-400 sm:text-lg">
            30 minutes, sans engagement. On analyse votre présence en ligne
            actuelle et on vous montre exactement comment un site conçu pour
            convertir peut rapporter plus qu'il ne coûte.
          </p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-center gap-3 text-sm text-gray-300">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15">
                <Video className="h-4 w-4 text-emerald-400" />
              </span>
              Par téléphone ou visio, à votre convenance
            </li>
            <li className="flex items-center gap-3 text-sm text-gray-300">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15">
                <Phone className="h-4 w-4 text-emerald-400" />
              </span>
              Vous préférez nous appeler ?{" "}
              <a href="tel:+14389696957" data-testid="booking-phone-link" className="font-semibold text-emerald-300 transition-colors hover:text-emerald-200">
                +1 438 969 6957
              </a>
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass emerald-glow rounded-2xl p-7 sm:p-9" data-testid="booking-card">
            {booked ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-14 text-center"
                data-testid="booking-success"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20">
                  <CalendarCheck className="h-8 w-8 text-emerald-400" />
                </span>
                <h3 className="mt-6 text-xl font-semibold text-white">Rendez-vous confirmé !</h3>
                <p className="mt-2 max-w-sm text-sm text-gray-400">
                  {fullFmt.format(selectedDay)} à {selectedTime.replace(":", "h")} avec{" "}
                  {booked.name.split(" ")[0]}. On vous contacte au{" "}
                  <span className="text-emerald-300">{booked.phone || booked.email}</span>.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form">
                <p className="font-mono-label mb-4 text-[10px]">01 — Choisissez une date</p>
                <div className="grid grid-cols-5 gap-2 sm:grid-cols-7" data-testid="booking-days">
                  {days.map((d) => {
                    const active = selectedDay && dateStr(d) === dateStr(selectedDay);
                    return (
                      <button
                        type="button"
                        key={d.toISOString()}
                        data-testid={`booking-day-${dateStr(d)}`}
                        onClick={() => setSelectedDay(d)}
                        className={`flex flex-col items-center rounded-xl border px-1 py-2.5 transition-all duration-200 ${
                          active
                            ? "border-emerald-400 bg-emerald-500/20 text-white"
                            : "border-white/8 bg-white/[0.03] text-gray-400 hover:border-emerald-400/40 hover:text-white"
                        }`}
                      >
                        <span className="text-[10px] uppercase tracking-wide">{dayFmt.format(d)}</span>
                        <span className="font-display mt-0.5 text-sm font-bold">{dateFmt.format(d)}</span>
                      </button>
                    );
                  })}
                </div>

                {selectedDay && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    <p className="font-mono-label mb-4 mt-7 text-[10px]">02 — Choisissez une heure</p>
                    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6" data-testid="booking-slots">
                      {SLOTS.map((slot) => {
                        const isTaken = taken.includes(slot);
                        const active = selectedTime === slot;
                        return (
                          <button
                            type="button"
                            key={slot}
                            data-testid={`booking-slot-${slot.replace(":", "")}`}
                            disabled={isTaken}
                            onClick={() => setSelectedTime(slot)}
                            className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-all duration-200 ${
                              isTaken
                                ? "cursor-not-allowed border-white/5 text-gray-600 line-through"
                                : active
                                  ? "border-emerald-400 bg-emerald-500/20 text-white"
                                  : "border-white/8 bg-white/[0.03] text-gray-300 hover:border-emerald-400/40"
                            }`}
                          >
                            {slot.replace(":", "h")}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {selectedTime && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    <p className="font-mono-label mb-4 mt-7 text-[10px]">03 — Vos coordonnées</p>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <input
                        data-testid="booking-name-input"
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Nom complet"
                        className={inputCls}
                      />
                      <input
                        data-testid="booking-email-input"
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="Courriel"
                        className={inputCls}
                      />
                      <input
                        data-testid="booking-phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="Téléphone (optionnel)"
                        className={inputCls}
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="booking-submit-button"
                      disabled={loading}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3.5 text-sm font-bold text-emerald-950 shadow-[0_8px_32px_rgba(16,185,129,0.35)] transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CalendarCheck className="h-4 w-4" />
                          Confirmer le {fullFmt.format(selectedDay)} à {selectedTime.replace(":", "h")}
                        </>
                      )}
                    </button>
                  </motion.div>
                )}
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
